import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  BatchActionsBar,
  BatchDetailHeader,
  BatchOutdatedBanner,
  BatchCompletedBanner,
  BatchFailedProcessingBanner,
  BatchDetailTabsHeader,
  type BatchTab,
  BatchDetailTabContent,
  BatchDetailModals,
  useBatchDetail,
} from "../components/batch";

interface BatchDetailProps {
  batchId?: string;
  onBatchUpdated?: () => void;
}

export function BatchDetail({ batchId: propBatchId, onBatchUpdated }: BatchDetailProps) {
  const { batch_id: routeBatchId } = useParams<{ batch_id?: string }>();
  const batchId = propBatchId || routeBatchId;
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<BatchTab>("changeset");
  const [changesetSubtab, setChangesetSubtab] = useState<"rules" | "knowledge" | "guardrails">("rules");

  // Modal open states
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

  const {
    batch,
    baselineRules,
    loading,
    actionLoading,
    excludedRuleIds,
    excludedKnowledgeIds,
    excludedGuardrailIds,
    toggleExcludeRule,
    toggleExcludeKnowledge,
    toggleExcludeGuardrail,
    loadBatchDetails,
    handleProcessBatch,
    handlePromoteBatch,
    handleRejectBatch,
    handleArchiveBatch,
    handleReopenBatch,
    handleRemoveComment,
    handleRemoveEvaluation,
    handleSaveRuleInPlace,
    handleSaveKnowledgeInPlace,
    handleSaveGuardrailInPlace,
  } = useBatchDetail(batchId, onBatchUpdated);

  if (!batchId) {
    return null;
  }

  const onConfirmPromote = async () => {
    await handlePromoteBatch();
    setIsPromoteModalOpen(false);
  };

  const onConfirmReject = async () => {
    await handleRejectBatch();
    setIsRejectModalOpen(false);
  };

  const onConfirmArchive = async () => {
    await handleArchiveBatch();
    setIsArchiveModalOpen(false);
  };

  const onConfirmReopen = async () => {
    await handleReopenBatch();
    setIsReopenModalOpen(false);
  };

  return (
    <div className="flex-1 min-w-0 h-full flex flex-col overflow-hidden bg-white dark:bg-stone-900">
      {/* Top Header */}
      <BatchDetailHeader
        batch={batch}
        loading={loading}
        onRefresh={loadBatchDetails}
        onOpenSimulator={() => setIsSimulatorOpen(true)}
        onOpenArchiveModal={() => setIsArchiveModalOpen(true)}
        onBack={() => navigate(`/batches${location.search}`)}
      />

      {/* Status Bar / Actions Strip */}
      {batch && (
        <BatchActionsBar
          batch={batch}
          actionLoading={actionLoading}
          onProcessBatch={handleProcessBatch}
          onOpenSimulator={() => setIsSimulatorOpen(true)}
          onOpenRejectModal={() => setIsRejectModalOpen(true)}
          onOpenPromoteModal={() => setIsPromoteModalOpen(true)}
          onOpenArchiveModal={() => setIsArchiveModalOpen(true)}
          onOpenReopenModal={() => setIsReopenModalOpen(true)}
        />
      )}

      {/* Outdated / Stale Batch Banner */}
      {batch && batch.status === "review_ready" && batch.is_stale && (
        <BatchOutdatedBanner
          staleReason={batch.stale_reason}
          actionLoading={actionLoading}
          onReopen={() => setIsReopenModalOpen(true)}
        />
      )}

      {/* Completed Batch Banner */}
      {batch && batch.status === "completed" && (
        <BatchCompletedBanner batch={batch} />
      )}

      {/* Failed Processing Banner */}
      {batch && batch.status === "failed_processing" && (
        <BatchFailedProcessingBanner
          errorMessage={batch.error_message || (batch.curation_run?.result_payload as any)?.error}
          runId={batch.run_id || batch.curation_run?.id}
          actionLoading={actionLoading}
          onReopen={() => setIsReopenModalOpen(true)}
        />
      )}

      {/* Detail Tabs Header */}
      <BatchDetailTabsHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        batch={batch}
      />

      {/* Content Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5 [scrollbar-gutter:stable]">
        <BatchDetailTabContent
          loading={loading}
          batch={batch}
          activeTab={activeTab}
          changesetSubtab={changesetSubtab}
          onChangeSubtab={setChangesetSubtab}
          excludedRuleIds={excludedRuleIds}
          excludedKnowledgeIds={excludedKnowledgeIds}
          excludedGuardrailIds={excludedGuardrailIds}
          onToggleExcludeRule={toggleExcludeRule}
          onToggleExcludeKnowledge={toggleExcludeKnowledge}
          onToggleExcludeGuardrail={toggleExcludeGuardrail}
          onSaveRule={handleSaveRuleInPlace}
          onSaveKnowledge={handleSaveKnowledgeInPlace}
          onSaveGuardrail={handleSaveGuardrailInPlace}
          baselineRules={baselineRules}
          onRemoveComment={handleRemoveComment}
          onRemoveEvaluation={handleRemoveEvaluation}
        />
      </div>

      {/* Modals & Drawers */}
      <BatchDetailModals
        batch={batch}
        excludedRuleIds={excludedRuleIds}
        excludedKnowledgeIds={excludedKnowledgeIds}
        excludedGuardrailIds={excludedGuardrailIds}
        actionLoading={actionLoading}
        isPromoteModalOpen={isPromoteModalOpen}
        onClosePromoteModal={() => setIsPromoteModalOpen(false)}
        onConfirmPromote={onConfirmPromote}
        isRejectModalOpen={isRejectModalOpen}
        onCloseRejectModal={() => setIsRejectModalOpen(false)}
        onConfirmReject={onConfirmReject}
        isArchiveModalOpen={isArchiveModalOpen}
        onCloseArchiveModal={() => setIsArchiveModalOpen(false)}
        onConfirmArchive={onConfirmArchive}
        isReopenModalOpen={isReopenModalOpen}
        onCloseReopenModal={() => setIsReopenModalOpen(false)}
        onConfirmReopen={onConfirmReopen}
        isSimulatorOpen={isSimulatorOpen}
        onCloseSimulator={() => setIsSimulatorOpen(false)}
      />
    </div>
  );
}
